'use client';

import { useEffect, useLayoutEffect, useRef } from 'react';

const STORAGE_PREFIX = 'cv:reload-scroll:';

/** One restore per document: a later soft navigation back to the page must not reuse it. */
let consumedThisDocument = false;

/**
 * The key of the page an in-app Back/Forward is heading to. The browser's
 * `popstate` fires before the router renders that page, so the page's first
 * mount can tell it was reached by Back/Forward and not by a link.
 */
let pendingPopKey: string | null = null;

const VISITOR_SCROLL_EVENTS = ['wheel', 'touchmove'] as const;
const SCROLL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' ']);

if (typeof window !== 'undefined') {
  window.addEventListener('popstate', () => {
    pendingPopKey = storageKey();
  });
}

function takePendingPop(key: string): boolean {
  const matches = pendingPopKey === key;
  pendingPopKey = null;
  return matches;
}

function storageKey(): string {
  return `${STORAGE_PREFIX}${window.location.pathname}${window.location.search}`;
}

function setRestoration(mode: ScrollRestoration) {
  try {
    window.history.scrollRestoration = mode;
  } catch {
    // Some embedded browsers refuse the write; the page still works, it just
    // falls back to the browser's own behaviour.
  }
}

function readSaved(key: string): number | null {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (raw == null) return null;
    const value = Number(raw);
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
}

function writeSaved(key: string, value: number) {
  try {
    window.sessionStorage.setItem(key, String(value));
  } catch {
    // Private mode or blocked storage: a reload lands at the top instead.
  }
}

function clearSaved(key: string) {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // Nothing to clean up if storage is unavailable.
  }
}

/** True when this document was opened by Reload or Back/Forward at this exact URL. */
function isReturnToThisPage(): boolean {
  const nav = window.performance?.getEntriesByType?.('navigation')?.[0] as
    | PerformanceNavigationTiming
    | undefined;
  if (!nav) return false;
  return (nav.type === 'reload' || nav.type === 'back_forward') && nav.name === window.location.href;
}

function saveCurrentPlace(key: string) {
  if (window.scrollY > 0) writeSaved(key, Math.round(window.scrollY));
  else clearSaved(key);
}

type PendingReturn = { target: number; fromPop: boolean };

/** Where to put the visitor back once the page is ready, if anywhere. */
function pickReturn(key: string, ready: boolean): PendingReturn | null {
  if (takePendingPop(key)) {
    // In-app Back/Forward to this page. The browser already scrolled as far
    // as the loading screen allows; only a place saved on leaving is right.
    const target = readSaved(key);
    if (target == null) return null;
    if (!ready) return { target, fromPop: true };
    window.scrollTo(0, target);
    clearSaved(key);
    return null;
  }
  if (consumedThisDocument || !isReturnToThisPage()) return null;
  consumedThisDocument = true;
  // Nothing saved but already scrolled means the browser restored on its
  // own (for example this entry was left by an in-app link, so pagehide
  // never ran for it): the position it already jumped to is the one to keep.
  const target = readSaved(key) ?? (window.scrollY > 0 ? Math.round(window.scrollY) : null);
  return target != null && !ready ? { target, fromPop: false } : null;
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

/** Page-level listeners; returns the function that removes them. */
function listenWhileMounted(
  key: string,
  readyRef: { current: boolean },
  visitorMovedRef: { current: boolean },
): () => void {
  const onPageHide = () => {
    setRestoration('manual');
    // Left while still loading: keep the position saved from the last time
    // the real page was on screen.
    if (readyRef.current) saveCurrentPlace(key);
  };
  // Restored from the back/forward cache: the page is exactly where it was,
  // so the browser can go back to handling scroll itself.
  const onPageShow = (event: PageTransitionEvent) => {
    if (event.persisted) setRestoration('auto');
  };
  // Back/Forward that stays on this same page (for example a change in the
  // query only) is not a new visit: the page is not mounted again.
  const onPopState = () => {
    if (pendingPopKey === key) pendingPopKey = null;
  };
  // After in-app Back/Forward the browser may still move the page on its own
  // after mount, so only real input counts as the visitor scrolling.
  const onVisitorInput = () => {
    visitorMovedRef.current = true;
  };
  const onKeyDown = (event: KeyboardEvent) => {
    if (!SCROLL_KEYS.has(event.key) || isTypingTarget(event.target)) return;
    visitorMovedRef.current = true;
  };

  window.addEventListener('pagehide', onPageHide);
  window.addEventListener('pageshow', onPageShow);
  window.addEventListener('popstate', onPopState);
  window.addEventListener('keydown', onKeyDown, { passive: true });
  for (const type of VISITOR_SCROLL_EVENTS) {
    window.addEventListener(type, onVisitorInput, { passive: true });
  }
  return () => {
    window.removeEventListener('pagehide', onPageHide);
    window.removeEventListener('pageshow', onPageShow);
    window.removeEventListener('popstate', onPopState);
    window.removeEventListener('keydown', onKeyDown);
    for (const type of VISITOR_SCROLL_EVENTS) {
      window.removeEventListener(type, onVisitorInput);
    }
  };
}

/**
 * Keeps a visitor's place on Reload for a page that shows a loading screen
 * first.
 *
 * The public program pages put the server-written summary for crawlers BELOW a
 * one-screen loading placeholder (`SummaryBelowPlaceholder`). The browser's own
 * scroll restore runs while that placeholder is up, so a visitor who reloads
 * part-way down lands in the plain-text summary instead of the placeholder.
 *
 * So, when the page is being left, this turns the browser's restore off for
 * this history entry and remembers the position itself. On the next load it
 * keeps the page at the top while `ready` is false, then scrolls back once the
 * real page has painted, and hands scrolling back to the browser.
 *
 * In-app Back/Forward has the same problem: the page mounts again, shows the
 * loading screen, and the browser's restore can only reach the bottom of that
 * short screen. So the position is also saved when the page is left by an
 * in-app link, and put back once the page is ready after in-app Back/Forward.
 *
 * Only the document unload path switches the browser restore off. In-app
 * navigation keeps it on 'auto', because in-app links copy the setting to the
 * next page.
 */
export function useKeepScrollAcrossReload(ready: boolean) {
  const readyRef = useRef(ready);
  readyRef.current = ready;
  const pendingRef = useRef<PendingReturn | null>(null);
  const visitorMovedRef = useRef(false);

  // Leaving by an in-app link or router push. Layout-effect cleanup runs before
  // the router removes this page and scrolls the next one to the top, so the
  // position read here is still the visitor's.
  useLayoutEffect(() => {
    const key = storageKey();
    return () => {
      if (readyRef.current) saveCurrentPlace(key);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const key = storageKey();

    const pending = pickReturn(key, readyRef.current);
    consumedThisDocument = true;
    if (pending) {
      pendingRef.current = pending;
      if (window.scrollY > 0) window.scrollTo(0, 0);
    }
    // Reload/full-page Back only. React's development double-run of effects
    // hands scrolling back in the cleanup below; take it again while a saved
    // place is still waiting.
    if (pendingRef.current && !pendingRef.current.fromPop) setRestoration('manual');

    const stopListening = listenWhileMounted(key, readyRef, visitorMovedRef);
    return () => {
      stopListening();
      // Leaving by an in-app link: in-app links copy this setting to the next
      // page, so give scrolling back to the browser whatever state we are in.
      setRestoration('auto');
    };
  }, []);

  useEffect(() => {
    if (!ready) return;
    const pending = pendingRef.current;
    if (pending == null) {
      // Nothing to put back. In-app links copy this setting to the next page,
      // so the browser must be in charge again before the visitor clicks one.
      setRestoration('auto');
      return;
    }
    pendingRef.current = null;
    // Effects run after React has put the real page in the document, so the
    // page is already its full height here.
    // The visitor already scrolled while it loaded: their choice wins.
    const visitorMoved = pending.fromPop ? visitorMovedRef.current : window.scrollY !== 0;
    if (!visitorMoved) window.scrollTo(0, pending.target);
    clearSaved(storageKey());
    setRestoration('auto');
  }, [ready]);
}

/** Test seam: each test is a fresh document. */
export function __resetKeepScrollAcrossReloadForTests() {
  consumedThisDocument = false;
  pendingPopKey = null;
}
